/**
 * useAMTasks — CRUD hook for AM task management
 *
 * Provides task fetching, creation, update, deletion with realtime subscriptions.
 * Tasks are scoped to the current AM via RLS.
 */

import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type TaskStatus = "todo" | "in_progress" | "done";
export type TaskPriority = "low" | "medium" | "high" | "urgent";
export type TaskSource = "manual" | "auto_health" | "auto_sla" | "auto_contract" | "auto_engagement";

export interface AMTask {
  id: string;
  amId: string;
  agentId: string | null;
  agentName?: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  source: TaskSource;
  sourceRef: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateTaskInput {
  title: string;
  description?: string;
  agentId?: string;
  priority?: TaskPriority;
  dueDate?: string;
  source?: TaskSource;
  sourceRef?: string;
}

function mapRow(row: any): AMTask {
  return {
    id: row.id,
    amId: row.am_id,
    agentId: row.agent_id,
    title: row.title,
    description: row.description,
    status: row.status as TaskStatus,
    priority: row.priority as TaskPriority,
    dueDate: row.due_date,
    source: row.source as TaskSource,
    sourceRef: row.source_ref,
    completedAt: row.completed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function useAMTasks(amId: string | null) {
  const [tasks, setTasks] = useState<AMTask[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all tasks for this AM
  const fetchTasks = useCallback(async () => {
    if (!amId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("am_tasks")
      .select("*")
      .eq("am_id", amId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[useAMTasks] Fetch error:", error);
    } else {
      setTasks((data || []).map(mapRow));
    }
    setLoading(false);
  }, [amId]);

  // Initial fetch + realtime subscription
  useEffect(() => {
    fetchTasks();

    if (!amId) return;

    const channel = supabase
      .channel(`am_tasks_${amId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "am_tasks", filter: `am_id=eq.${amId}` },
        (payload) => {
          if (payload.eventType === "INSERT") {
            setTasks((prev) => [mapRow(payload.new), ...prev]);
          } else if (payload.eventType === "UPDATE") {
            setTasks((prev) =>
              prev.map((t) => (t.id === payload.new.id ? mapRow(payload.new) : t))
            );
          } else if (payload.eventType === "DELETE") {
            setTasks((prev) => prev.filter((t) => t.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [amId, fetchTasks]);

  const createTask = useCallback(
    async (input: CreateTaskInput) => {
      if (!amId) return;

      const { error } = await supabase.from("am_tasks").insert({
        am_id: amId,
        agent_id: input.agentId || null,
        title: input.title,
        description: input.description || null,
        priority: input.priority || "medium",
        due_date: input.dueDate || null,
        source: input.source || "manual",
        source_ref: input.sourceRef || null,
      });

      if (error) {
        console.error("[useAMTasks] Create error:", error);
        throw error;
      }
    },
    [amId]
  );

  const updateTask = useCallback(
    async (id: string, updates: Partial<Pick<AMTask, "title" | "description" | "status" | "priority" | "dueDate" | "agentId">>) => {
      const payload: Record<string, any> = { updated_at: new Date().toISOString() };
      if (updates.title !== undefined) payload.title = updates.title;
      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.priority !== undefined) payload.priority = updates.priority;
      if (updates.dueDate !== undefined) payload.due_date = updates.dueDate;
      if (updates.agentId !== undefined) payload.agent_id = updates.agentId;
      if (updates.status !== undefined) {
        payload.status = updates.status;
        payload.completed_at = updates.status === "done" ? new Date().toISOString() : null;
      }

      const { error } = await supabase
        .from("am_tasks")
        .update(payload)
        .eq("id", id);

      if (error) {
        console.error("[useAMTasks] Update error:", error);
        throw error;
      }
    },
    []
  );

  const moveTask = useCallback(
    async (id: string, newStatus: TaskStatus) => {
      await updateTask(id, { status: newStatus });
    },
    [updateTask]
  );

  const deleteTask = useCallback(async (id: string) => {
    const { error } = await supabase
      .from("am_tasks")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("[useAMTasks] Delete error:", error);
      throw error;
    }
  }, []);

  return { tasks, loading, createTask, updateTask, moveTask, deleteTask, refetch: fetchTasks };
}
